/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { CheckoutModule, ErrorLocation, ICheckoutState } from '@msdyn365-commerce/global-state';
import { getEstimatedPickupTimeslotAvailabilitiesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';
import {
    CartLine,
    LineDeliverySpecification,
    OrgUnitLocation,
    PickupTimeslotAvailability,
    PickupTimeslotAvailabilitySearchCriteria,
    ProductDeliveryOptions,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import {
    ArrayExtensions,
    getFallbackImageUrl,
    getSimpleProducts,
    ObjectExtensions,
    ProductInput
} from '@msdyn365-commerce-modules/retail-actions';
import { format, IModuleProps, isNullOrWhiteSpace, Waiting } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import { action, computed, observable, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { ICheckoutPickupData } from './checkout-pickup.data';
import { ICheckoutPickupProps } from './checkout-pickup.props.autogenerated';
import {
    CheckoutPickupDateDropdown,
    CheckoutPickupError,
    CheckoutPickupTimeslotDropdown,
    getDate,
    getTimeslot,
    getUTCDateWithoutTime
} from './components';
import {
    CheckoutPickupAddress,
    CheckoutPickupCartLines,
    CheckoutPickupGroupHeader,
    CheckoutPickupTimeslotInfo,
    DefaultImageSettings,
    ICheckoutPickupAddressProps,
    ICheckoutPickupCartLineInfo,
    ICheckoutPickupCartLinesProps,
    ICheckoutPickupGroup,
    ICheckoutPickupGroupHeaderProps,
    ICheckoutPickupGroupViewProps,
    ICheckoutPickupTimeslotInfoProps,
    PickupTimeslotAvailabilityState
} from './components/checkout-pickup-group';
import { focusOnCheckoutError } from '../../checkout-errors';

export interface ICheckoutPickupModuleProps extends ICheckoutPickupProps<ICheckoutPickupData>, IModuleStateProps {}

export interface ICheckoutPickupState {
    pickupGroups: ICheckoutPickupGroup[];
}

export interface ICheckoutPickupViewState {
    isLoading: boolean;
    isHeaderError: boolean;
    isShowDropdown: boolean;
    isShowSelected: boolean;
}

export interface ICheckoutPickupViewProps extends ICheckoutPickupProps<{}> {
    checkoutPickup: IModuleProps;
    viewState: ICheckoutPickupViewState;
    headerError: React.ReactNode;
    waiting: React.ReactNode;
    pickupGroups: ICheckoutPickupGroupViewProps[];
    checkoutErrorRef?: React.RefObject<HTMLElement>;
}

/**
 *
 * CheckoutPickup component.
 * @extends {React.Component<ICheckoutPickupModuleProps>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutPickup extends React.Component<ICheckoutPickupModuleProps, ICheckoutPickupState> {
    private readonly checkoutErrorRef: React.RefObject<HTMLElement> = React.createRef();

    @observable private isLoading: boolean = false;

    @observable private headerErrorMessage?: string;

    @computed get isDataReady(): boolean {
        return (
            (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS' &&
            (this.props.data.orgUnitLocations.result && this.props.data.orgUnitLocations.status) === 'SUCCESS'
        );
    }

    constructor(props: ICheckoutPickupModuleProps) {
        super(props);
        this.state = { pickupGroups: [] };

        this.setDateHandler = this.setDateHandler.bind(this);
        this.setTimeslotHandler = this.setTimeslotHandler.bind(this);
    }

    public componentDidMount(): void {
        when(
            () => this.isDataReady,
            async () => {
                await this.initPickupGroup();
                this.initModuleState();
            }
        );

        if (this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging) {
            reaction(
                () => this.props.data.checkout.result?.checkoutError,
                checkoutError => {
                    if (checkoutError && checkoutError.errorLocation === ErrorLocation.CheckoutPickup && checkoutError.errorMessage) {
                        this.setError(checkoutError.errorMessage);
                    }
                }
            );

            reaction(
                () => this.props.data.checkout.result?.checkoutErrorFocus,
                checkoutErrorFocus => {
                    if (checkoutErrorFocus === CheckoutModule.CheckoutPickup) {
                        focusOnCheckoutError(this.checkoutErrorRef, this.props.context.actionContext);
                    }
                }
            );
        }
    }

    public shouldComponentUpdate(nextProps: ICheckoutPickupModuleProps, nextState: ICheckoutPickupState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        if (!this.isDataReady || !this.canPickup()) {
            return null;
        }

        const { resources, config, context } = this.props;

        const {
            pickupDateLabel,
            pickupTimeslotLabel,
            pickupDateDefaultText,
            pickupTimeslotDefaultText,
            itemsText,
            singleItemText,
            pickupDateTimeslotText,
            pickupTimeslotPlaceHolder,
            pickupLocationLabel
        } = resources;

        const { locale } = context.request;
        const { className, imageSettings, useHour12TimeFormat } = config;
        const defaultClassName = 'ms-checkout-pickup';
        const groupsViewProps: ICheckoutPickupGroupViewProps[] = [];

        this.state.pickupGroups.forEach((value, index) => {
            // Divider line for each pickup group
            const groupDivider: React.ReactNode = index !== 0 && <hr className={`${defaultClassName}__group-divider`} />;

            // Line images
            const cartlines: ICheckoutPickupCartLineInfo[] = value.cartLines
                .filter(x => !ObjectExtensions.isNullOrUndefined(x.product))
                .map(line => ({
                    lineId: line.cartLine.LineId || '',
                    imageProps: {
                        requestContext: context.actionContext.requestContext,
                        className: `${defaultClassName}__group-line-image`,
                        altText: line.product?.Name,
                        src: line.product?.PrimaryImageUrl || '',
                        fallBackSrc: getFallbackImageUrl(
                            line.product?.ItemId,
                            context.actionContext.requestContext.apiSettings,
                            context.request.app?.config?.OmniChannelMedia
                        ),
                        gridSettings: context.request.gridSettings!,
                        imageSettings: imageSettings || DefaultImageSettings,
                        loadFailureBehavior: 'empty'
                    },
                    quantity: line.cartLine.Quantity || 0
                }));

            // Heading for each group
            const numberOfCartLines = this._countItems(cartlines);
            const itemText = numberOfCartLines > 1 ? itemsText : singleItemText;
            const itemTitle = `(${format(itemText, numberOfCartLines)})`;
            const headerProps: ICheckoutPickupGroupHeaderProps = {
                moduleClassName: defaultClassName,
                storeInfo: `${value.pickupModeName}, ${value.storeName}`,
                items: itemTitle
            };
            const groupHeader: React.ReactNode = <CheckoutPickupGroupHeader {...headerProps} />;

            const lineImageProps: ICheckoutPickupCartLinesProps = {
                moduleClassName: defaultClassName,
                cartLines: cartlines.filter(x => !ObjectExtensions.isNullOrUndefined(x))
            };
            const cartLineImages: React.ReactNode = <CheckoutPickupCartLines {...lineImageProps} />;

            // Selected pickup date and timeslot node
            const dateString = getDate(value.selectedDate, locale);
            const timeslotString = getTimeslot(
                pickupTimeslotPlaceHolder,
                value.selectedTimeslot?.StartDateTime,
                value.selectedTimeslot?.EndDateTime,
                locale,
                useHour12TimeFormat
            );
            const timeslotInfoProps: ICheckoutPickupTimeslotInfoProps = {
                moduleClassName: defaultClassName,
                title: pickupDateTimeslotText,
                content: `${dateString} ${timeslotString}`
            };
            const selectedDateTimeslotNode: React.ReactNode = <CheckoutPickupTimeslotInfo {...timeslotInfoProps} />;

            // Pickup address
            const addressProps: ICheckoutPickupAddressProps = {
                moduleClassName: defaultClassName,
                title: pickupLocationLabel,
                address: value.pickupAddress
            };
            const pickupAddress: React.ReactNode = <CheckoutPickupAddress {...addressProps} />;

            // Pickup date and timeslot dropdown list
            const dropdownClassName = `${defaultClassName}__group-datetime-dropdown`;
            const labelClassName = `${dropdownClassName}-label`;
            const dropdownSelectClassName = `${dropdownClassName}-select`;
            const dateDropdownNode: React.ReactNode = (
                <CheckoutPickupDateDropdown
                    className={dropdownClassName}
                    pickupStoreId={value.pickupStoreId}
                    deliveryMode={value.deliveryMode}
                    defaultText={pickupDateDefaultText}
                    errorMessage={value.dateErrorMessage}
                    labelText={pickupDateLabel}
                    locale={locale}
                    dropdownId={`pickup-date-dropdown-${index}`}
                    availableDates={value.availableDates}
                    selectedDate={value.selectedDate}
                    controlledByParent
                    labelClassname={labelClassName}
                    dropdownClassname={dropdownSelectClassName}
                    setDateHandler={this.setDateHandler}
                />
            );

            const timeslotDropdownNode: React.ReactNode = (
                <CheckoutPickupTimeslotDropdown
                    className={dropdownClassName}
                    pickupStoreId={value.pickupStoreId}
                    deliveryMode={value.deliveryMode}
                    defaultText={pickupTimeslotDefaultText}
                    errorMessage={value.timeslotErrorMessage}
                    labelText={pickupTimeslotLabel}
                    timeslotPlaceHolder={pickupTimeslotPlaceHolder}
                    locale={locale}
                    dropdownId={`pickup-timeslot-dropdown-${index}`}
                    availableTimeslots={value.availableTimeslots}
                    selectedTimeslot={value.selectedTimeslot}
                    controlledByParent
                    labelClassname={labelClassName}
                    dropdownClassname={dropdownSelectClassName}
                    setTimeslotHandler={this.setTimeslotHandler}
                    useHour12TimeFormat={useHour12TimeFormat}
                />
            );

            const viewProp: ICheckoutPickupGroupViewProps = {
                moduleProps: { ...this.props },
                pickupGroupContainer: { className: `${defaultClassName}__group` },
                dateTimeslotContainer: { className: `${defaultClassName}__group-datetime` },
                pickupStoreId: value.pickupStoreId,
                pickupModeName: value.pickupModeName,
                deliveryMode: value.deliveryMode,
                groupDivider,
                groupHeader,
                groupErrorMessage: <CheckoutPickupError errorMessage={value.groupErrorMessage} />,
                cartLineImages,
                pickupDateDropdown: dateDropdownNode,
                pickupTimeslotDropdown: timeslotDropdownNode,
                selectedDateTimeslot: selectedDateTimeslotNode,
                pickupAddress
            };

            groupsViewProps.push(viewProp);
        });

        const {
            moduleState: { isReady, hasError }
        } = this.props;
        const isHeaderError = !this.isLoading && this.headerErrorMessage !== undefined;
        const isShowDropdown = !this.isLoading && !isReady;
        const isShowSelected = !this.isLoading && !hasError && isReady;

        const viewProps: ICheckoutPickupViewProps = {
            ...this.props,
            checkoutPickup: {
                moduleProps: this.props,
                className: classname(defaultClassName, className)
            },
            viewState: {
                isLoading: this.isLoading,
                isHeaderError,
                isShowSelected,
                isShowDropdown
            },
            headerError: <CheckoutPickupError errorMessage={this.headerErrorMessage} />,
            waiting: <Waiting />,
            pickupGroups: groupsViewProps,
            checkoutErrorRef: this.checkoutErrorRef
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    @action
    public async setDateHandler(pickupStoreId: string, deliveryMode: string, pickupDate: Date | undefined): Promise<void> {
        const oldGroup = this.state.pickupGroups.find(
            group => group.pickupStoreId === pickupStoreId && group.deliveryMode === deliveryMode
        );
        if (!oldGroup) {
            return;
        }

        const newGroup: ICheckoutPickupGroup = { ...oldGroup };
        newGroup.selectedDate = pickupDate;
        newGroup.selectedTimeslot = undefined; // Clear the selected timeslot in any case
        newGroup.timeslotErrorMessage = undefined; // Clear the timeslot error message if any
        newGroup.lineDeliverySpecs.forEach((lineSpec: LineDeliverySpecification) => {
            if (lineSpec.DeliverySpecification) {
                lineSpec.DeliverySpecification.PickupTimeslotStartDateTime = undefined;
                lineSpec.DeliverySpecification.PickupTimeslotEndDateTime = undefined;
            }
        });

        if (pickupDate) {
            // Date is set
            const searchCriteria: PickupTimeslotAvailabilitySearchCriteria = {
                DeliveryModeCode: newGroup.deliveryMode,
                StoreId: newGroup.pickupStoreId,
                StartDate: pickupDate,
                EndDate: pickupDate,
                SearchOptionValue: 2 // Test runtime does not recognize PickupTimeslotAvailabilitySearchOption.All
            };
            const timeslotResults: PickupTimeslotAvailability[] = await getEstimatedPickupTimeslotAvailabilitiesAsync(
                { callerContext: this.props.context.actionContext, queryResultSettings: {}, bypassCache: 'get' },
                searchCriteria
            );

            newGroup.availableTimeslots = timeslotResults.filter(value => (value.AvailableCapacity || 0) > 0);
            newGroup.dateErrorMessage =
                newGroup.availableTimeslots.length === 0 ? this.props.resources.noAvailableTimeslotError : undefined;
        } else {
            // Date is not set
            newGroup.availableTimeslots = [];
            newGroup.dateErrorMessage = undefined;
        }

        const groups = this.state.pickupGroups.map(group => {
            if (group.pickupStoreId === pickupStoreId && group.deliveryMode === deliveryMode) {
                return newGroup;
            }
            return group;
        });

        this.setState({ pickupGroups: groups });
    }

    @action
    public setTimeslotHandler(pickupStoreId: string, deliveryMode: string, pickupTimeslot: PickupTimeslotAvailability | undefined): void {
        const oldGroup = this.state.pickupGroups.find(
            group => group.pickupStoreId === pickupStoreId && group.deliveryMode === deliveryMode
        );
        if (!oldGroup) {
            return;
        }

        const newGroup: ICheckoutPickupGroup = { ...oldGroup };

        newGroup.selectedTimeslot = pickupTimeslot;
        newGroup.lineDeliverySpecs.forEach((lineSpec: LineDeliverySpecification) => {
            if (lineSpec.DeliverySpecification) {
                lineSpec.DeliverySpecification.PickupTimeslotStartDateTime = pickupTimeslot?.StartDateTime;
                lineSpec.DeliverySpecification.PickupTimeslotEndDateTime = pickupTimeslot?.EndDateTime;
            }
        });

        if (pickupTimeslot) {
            newGroup.timeslotErrorMessage = undefined;
        }

        const groups = this.state.pickupGroups.map(group => {
            if (group.pickupStoreId === pickupStoreId && group.deliveryMode === deliveryMode) {
                return newGroup;
            }
            return group;
        });

        this.setState({ pickupGroups: groups });
    }

    @action
    private readonly initModuleState = (): void => {
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onSubmit: this.onSubmit,
            status: this.canPickup() ? (this.isPickupTimeSlotSet() ? 'ready' : 'updating') : 'disabled'
        });
    };

    private readonly onEdit = (): void => {
        this.props.moduleState.onUpdating();
    };

    private readonly onReady = (): void => {
        this.props.moduleState.onReady();
    };

    private readonly onPending = (): void => {
        this.props.moduleState.onPending();
    };

    private readonly onSubmit = async (): Promise<void> => {
        await this.updateCartLinePickupDateTimeslot();
    };

    private readonly setError = (errorMessage: string, isHeaderError: boolean = true): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        if (isHeaderError) {
            this.headerErrorMessage = errorMessage;
        }
    };

    private readonly clearError = (): void => {
        this.props.moduleState.setHasError(false);
        this.headerErrorMessage = undefined;
    };

    @action
    private readonly initPickupGroup = async (): Promise<void> => {
        const pickupCartLines: CartLine[] = this.getCartLinesforPickup();
        let products: SimpleProduct[] = [];
        try {
            products = await this.getProductsByCartLines(
                this.props.data.checkout.result?.checkoutCart.cart.ChannelId || 0,
                pickupCartLines
            );
        } catch (error) {
            this.props.telemetry.error(error);
        }
        const pickupGroups: ICheckoutPickupGroup[] = [];
        const locations: OrgUnitLocation[] = this.props.data.orgUnitLocations.result || [];
        this.isLoading = true;
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        const { featureState, deliveryOptions } = this.props?.data;
        const retailMultiplePickUpOptionEnabled = featureState?.result?.find(feature => feature.Name === multiplePickupStoreSwitchName)
            ?.IsEnabled;

        for (const line of pickupCartLines) {
            const group: ICheckoutPickupGroup | undefined = pickupGroups.find(
                x => x.pickupStoreId === line.FulfillmentStoreId && x.deliveryMode === line.DeliveryMode
            );
            const product: SimpleProduct | undefined = products.find(x => x.RecordId === line.ProductId);
            const lineDeliverySpecification: LineDeliverySpecification = {
                LineId: line.LineId,
                DeliverySpecification: {
                    DeliveryModeId: line.DeliveryMode,
                    DeliveryPreferenceTypeValue: 2,
                    PickUpStoreId: line.FulfillmentStoreId,
                    PickupTimeslotStartDateTime: line.PickupTimeslotStartDateTime,
                    PickupTimeslotEndDateTime: line.PickupTimeslotEndDateTime,
                    DeliveryAddress: line.ShippingAddress
                }
            };
            if (group) {
                group.lineDeliverySpecs.push(lineDeliverySpecification);
                group.cartLines.push({ cartLine: line, product });
            } else {
                // Group not exists yet
                const location: OrgUnitLocation | undefined = locations.find(x => x.OrgUnitNumber === line.FulfillmentStoreId);
                const storeName: string = this.getStoreName(location, line);
                const selectedDate: Date | undefined =
                    line.PickupTimeslotStartDateTime && getUTCDateWithoutTime(line.PickupTimeslotStartDateTime);
                const selectedTimeslot =
                    line.PickupTimeslotStartDateTime && line.PickupTimeslotEndDateTime
                        ? { StartDateTime: line.PickupTimeslotStartDateTime, EndDateTime: line.PickupTimeslotEndDateTime }
                        : undefined;
                const newGroup: ICheckoutPickupGroup = {
                    pickupModeName: retailMultiplePickUpOptionEnabled
                        ? this.getPickupModeDescription(
                              this.props.resources.generalPickupText,
                              line.DeliveryMode,
                              deliveryOptions?.result,
                              product
                          )
                        : this.props.resources.generalPickupText,
                    pickupStoreId: line.FulfillmentStoreId || '',
                    deliveryMode: line.DeliveryMode || '',
                    storeName,
                    pickupAddress: line.ShippingAddress,
                    availabilityState: PickupTimeslotAvailabilityState.None,
                    lineDeliverySpecs: [lineDeliverySpecification],
                    cartLines: [{ cartLine: line, product }],
                    availableDates: [],
                    availableTimeslots: [],
                    selectedDate,
                    selectedTimeslot
                };

                let dailyGroupingResults: PickupTimeslotAvailability[] = [];
                try {
                    const searchCriteria: PickupTimeslotAvailabilitySearchCriteria = {
                        DeliveryModeCode: newGroup.deliveryMode,
                        StoreId: newGroup.pickupStoreId,
                        SearchOptionValue: 1 // Test runtime does not recognize PickupTimeslotAvailabilitySearchOption.DailyGroupingTimeslotOnly
                    };
                    dailyGroupingResults = await getEstimatedPickupTimeslotAvailabilitiesAsync(
                        { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                        searchCriteria
                    );
                } catch (error) {
                    this.props.telemetry.error(error);
                }
                newGroup.availabilityState = this.getPickupTimeslotAvailabilityState(dailyGroupingResults);
                if (newGroup.availabilityState === PickupTimeslotAvailabilityState.NotAvailable) {
                    newGroup.groupErrorMessage = this.props.resources.noAvailableDateError;
                    this.setError(newGroup.groupErrorMessage, false);
                }
                dailyGroupingResults.forEach(value => {
                    if (value.StartDateTime && (value.AvailableCapacity || 0) > 0) {
                        newGroup.availableDates.push(getUTCDateWithoutTime(value.StartDateTime));
                    }
                });

                if (selectedDate) {
                    let timeslotResults: PickupTimeslotAvailability[] = [];
                    try {
                        const searchCriteria: PickupTimeslotAvailabilitySearchCriteria = {
                            DeliveryModeCode: newGroup.deliveryMode,
                            StoreId: newGroup.pickupStoreId,
                            StartDate: selectedDate,
                            EndDate: selectedDate,
                            SearchOptionValue: 2 // Test runtime does not recognize PickupTimeslotAvailabilitySearchOption.All
                        };
                        timeslotResults = await getEstimatedPickupTimeslotAvailabilitiesAsync(
                            { callerContext: this.props.context.actionContext, queryResultSettings: {}, bypassCache: 'get' },
                            searchCriteria
                        );
                    } catch (error) {
                        this.props.telemetry.error(error);
                    }
                    newGroup.availableTimeslots = timeslotResults.filter(value => (value.AvailableCapacity || 0) > 0);
                }
                pickupGroups.push(newGroup);
            }
        }

        const filteredPickupGroups = pickupGroups.filter(
            group =>
                group.availabilityState === PickupTimeslotAvailabilityState.Available ||
                group.availabilityState === PickupTimeslotAvailabilityState.NotAvailable
        );

        if (!ArrayExtensions.hasElements(filteredPickupGroups)) {
            this.props.context.telemetry.error('Data is not ready or pickup group is empty, module wont render.');
        }
        this.isLoading = false;
        this.setState({ pickupGroups: filteredPickupGroups });
    };

    @action
    private readonly updateCartLinePickupDateTimeslot = async (): Promise<void> => {
        if (!this.validateTimeslot()) {
            return;
        }
        const { resources } = this.props;
        let lineSpecs: LineDeliverySpecification[] = [];

        this.state.pickupGroups.forEach(group => {
            lineSpecs = lineSpecs.concat(group.lineDeliverySpecs);
        });

        this.clearError();
        this.onPending();

        try {
            const checkoutState = this.props.data.checkout.result as ICheckoutState;
            const response = await checkoutState.checkoutCart.updateCartLinePickupDateTimeslot({ lineDeliverySpecifications: lineSpecs });

            if (response.status === 'FAILED') {
                this.setError(resources.genericErrorMessage);
            } else {
                this.onReady();
                this.props.moduleState.setIsCancellable(true);
            }
        } catch {
            this.setError(resources.genericErrorMessage);
        }
    };

    private readonly getProductsByCartLines = async (channelId: number, cartLines: CartLine[]): Promise<SimpleProduct[]> => {
        const actionContext = this.props.context.actionContext;
        const productInputs = cartLines
            .filter(line => !ObjectExtensions.isNullOrUndefined(line.ProductId))
            .map(
                line =>
                    new ProductInput(
                        line.ProductId || 0,
                        actionContext.requestContext.apiSettings,
                        channelId,
                        undefined,
                        actionContext.requestContext
                    )
            );
        return getSimpleProducts(productInputs, actionContext);
    };

    private readonly validateTimeslot = (): boolean => {
        let validationResult = true;
        this.state.pickupGroups.forEach(group => {
            switch (group.availabilityState) {
                case PickupTimeslotAvailabilityState.NotAvailable:
                    // Need customer to select another store in this case
                    validationResult = false;
                    break;
                case PickupTimeslotAvailabilityState.Available:
                    if (!group.selectedDate || !group.selectedTimeslot) {
                        if (!group.selectedDate) {
                            group.dateErrorMessage = this.props.resources.pickupDateRequired;
                        }
                        if (!group.selectedTimeslot) {
                            group.timeslotErrorMessage = this.props.resources.pickupTimeslotRequired;
                        }
                        this.setError(this.props.resources.pickupDateTimeslotRequired, false);
                        validationResult = false;
                    } else {
                        group.dateErrorMessage = undefined;
                        group.timeslotErrorMessage = undefined;
                    }
                    break;

                // We consider timeslot not defined as fallback case
                case PickupTimeslotAvailabilityState.NotDefined:
                default:
            }
        });

        return validationResult;
    };

    private readonly getStoreName = (orgUnitLocation: OrgUnitLocation | undefined, cartLine: CartLine): string => {
        return orgUnitLocation?.OrgUnitName || cartLine.ShippingAddress?.Name || cartLine.FulfillmentStoreId || '';
    };

    private readonly canPickup = (): boolean => {
        return ArrayExtensions.hasElements(this.state.pickupGroups);
    };

    private readonly isPickupTimeSlotSet = (): boolean => {
        return (
            ArrayExtensions.hasElements(this.state.pickupGroups) &&
            this.state.pickupGroups.every(
                group =>
                    !ObjectExtensions.isNullOrUndefined(group.selectedTimeslot?.StartDateTime) &&
                    !ObjectExtensions.isNullOrUndefined(group.selectedTimeslot?.EndDateTime)
            )
        );
    };

    private readonly getCartLinesforPickup = (): CartLine[] => {
        return this.props.data.checkout.result?.checkoutCart.cart.CartLines?.filter(line => this.isPickupLine(line)) || [];
    };

    private readonly isPickupMode = (deliveryMode: string | undefined): boolean => {
        const pickupDeliveryModeCode = this.props.context.request.channel?.PickupDeliveryModeCode;
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        const { channelDeliveryOptionConfig, featureState } = this.props?.data;
        const retailMultiplePickUpOptionEnabled = featureState?.result?.find(feature => feature.Name === multiplePickupStoreSwitchName)
            ?.IsEnabled;
        if (retailMultiplePickUpOptionEnabled && deliveryMode !== undefined) {
            const pickupDeliveryMode = channelDeliveryOptionConfig?.result?.PickupDeliveryModeCodes?.includes(deliveryMode);
            return pickupDeliveryMode !== undefined ? pickupDeliveryMode : false;
        }
        return deliveryMode !== undefined && pickupDeliveryModeCode !== undefined && deliveryMode === pickupDeliveryModeCode;
    };

    private readonly isPickupLine = (line: CartLine): boolean => {
        return (
            this.isPickupMode(line.DeliveryMode) && line.FulfillmentStoreId !== undefined && !isNullOrWhiteSpace(line.FulfillmentStoreId)
        );
    };

    private readonly getPickupModeDescription = (
        resourceString: string,
        deliveryMode?: string,
        deliveryOptions?: ProductDeliveryOptions[],
        product?: SimpleProduct
    ): string => {
        let deliveryOption: string = resourceString;
        if (ArrayExtensions.hasElements(deliveryOptions) && product !== undefined) {
            let foundProductDeliveryOption;
            foundProductDeliveryOption = deliveryOptions?.find(deliveryO => {
                return deliveryO && deliveryO.ProductId === product.RecordId;
            });
            const deliveryDesc = foundProductDeliveryOption?.DeliveryOptions
                ? foundProductDeliveryOption?.DeliveryOptions?.find(option => option.Code === deliveryMode)?.Description
                : resourceString;
            if (deliveryDesc !== undefined) {
                deliveryOption = deliveryDesc;
            }
        }
        return deliveryOption;
    };

    private readonly getPickupTimeslotAvailabilityState = (dailyResult: PickupTimeslotAvailability[]) => {
        let state: PickupTimeslotAvailabilityState = PickupTimeslotAvailabilityState.None;
        if (!ArrayExtensions.hasElements(dailyResult)) {
            state = PickupTimeslotAvailabilityState.NotDefined;
        } else if (dailyResult.every(date => (date.AvailableCapacity || 0) <= 0)) {
            state = PickupTimeslotAvailabilityState.NotAvailable;
        } else {
            state = PickupTimeslotAvailabilityState.Available;
        }
        return state;
    };

    /**
     * Returns count of products.
     * @param cartLine - The checkout pickup cart line info props.
     * @returns Number of products.
     */
    private readonly _countItems = (cartlines: ICheckoutPickupCartLineInfo[]): number => {
        return cartlines.reduce((count, line) => {
            return count + (line.quantity || 0);
        }, 0);
    };
}

export default CheckoutPickup;
